import { apiDelete, apiGet, apiPatch, apiPost, type QueryRecord } from './http-client';

/** Подмножество полей слота для списка расписания (совпадает с SlotResponse на API). */
export type ScheduleSlotListItem = {
  id: number;
  service_id: number;
  service_name: string;
  trainer_user_id: number | null;
  /** @deprecated один релиз: алиас trainer_user_id для старых вызовов */
  trainer_id?: number | null;
  trainer_name: string | null;
  trainer_photo_url: string | null;
  slot_date: string;
  start_time: string;
  end_time: string;
  max_participants: number;
  places_left: number;
  service_photo_url: string | null;
  service_price: number | null;
  /** services.payment_required: нужна ли оплата услуги (false — запись без платёжного шага). */
  service_payment_required?: boolean;
  service_cancellation_deadline_minutes: number | null;
  service_group_type: string | null;
  /** Цвет типа услуги (#RRGGBB), если задан в CRM. Приоритет над service_color. */
  service_type_color: string | null;
  /** Цвет самой услуги (#RRGGBB), если задан в CRM. */
  service_color: string | null;
  is_available: boolean;
  /** Аренда на весь день */
  is_all_day?: boolean;
  /** Услуга типа «Аренда» */
  is_rental?: boolean;
  /** Id филиала: с API (если появится) или проставляется на клиенте по контексту запроса. */
  branch_id?: number | null;
  /** Пространство (зал), в котором проходит занятие; null — без пространства. */
  space_id?: number | null;
  space_name?: string | null;
  space_capacity?: number | null;
  /** Подпись филиала (embed: режим «все салоны», слоты из нескольких запросов). */
  branch_label?: string | null;
  /** Только manage=true: комментарии к записям (клиент / CRM). */
  booking_comments?: { client_name: string; comment: string }[] | null;
};

/**
 * Сеть с несколькими филиалами: параллельные запросы по каждому branch_id и объединение.
 * (На API для is_network без branch_id список пустой.)
 */
export async function fetchScheduleSlotsAllBranches(params: {
  organizationId: number | null;
  branchList: { id: number; name: string }[];
  fromDate: string;
  toDate: string;
  accessToken: string | null;
  serviceId?: number | null;
  /** true — вернуть и скрытые для записи слоты (нужно право schedule:staff_write / bookings:staff_write). */
  manage?: boolean;
}): Promise<ScheduleSlotListItem[]> {
  const lists = await Promise.all(
    params.branchList.map(async (b) => {
      const slots = await fetchScheduleSlots({
        organizationId: params.organizationId,
        branchId: b.id,
        fromDate: params.fromDate,
        toDate: params.toDate,
        accessToken: params.accessToken,
        serviceId: params.serviceId,
        manage: params.manage
      });
      return slots.map((s) => ({ ...s, branch_id: b.id, branch_label: b.name }));
    })
  );
  const flat = lists.flat();
  const byId = new Map<number, ScheduleSlotListItem>();
  for (const s of flat) {
    if (!byId.has(s.id)) {
      byId.set(s.id, s);
    }
  }
  return [...byId.values()].sort((a, b) => {
    const da = `${a.slot_date}T${a.start_time}`;
    const db = `${b.slot_date}T${b.start_time}`;
    return da.localeCompare(db);
  });
}

/**
 * GET /api/v1/schedule/slots
 * `organization_id` передаём всегда, когда организация известна: для суперадмина это
 * выбранная студия (`me.organization_id`), для клиента/тренера — студия его контекста.
 */
export function fetchScheduleSlots(params: {
  organizationId: number | null;
  branchId: number | null;
  fromDate: string;
  toDate: string;
  accessToken: string | null;
  /** Фильтр по услуге (совпадает с query `service_id` на API). */
  serviceId?: number | null;
  /** true — вернуть и скрытые для записи слоты (при наличии права). */
  manage?: boolean;
}): Promise<ScheduleSlotListItem[]> {
  const q: QueryRecord = {
    from_date: params.fromDate,
    to_date: params.toDate
  };
  if (params.manage) {
    q.manage = true;
  }
  if (params.organizationId != null) {
    q.organization_id = params.organizationId;
  }
  if (params.branchId != null) {
    q.branch_id = params.branchId;
  }
  if (params.serviceId != null && params.serviceId > 0) {
    q.service_id = params.serviceId;
  }
  return apiGet<ScheduleSlotListItem[]>('/api/v1/schedule/slots', q, {
    accessToken: params.accessToken ?? undefined
  }).then((rows) =>
    rows.map((s) => normalizeScheduleSlotListItem(s, params.branchId))
  );
}

function normalizeScheduleSlotListItem(
  row: ScheduleSlotListItem & { trainer_id?: number | null },
  branchId: number | null
): ScheduleSlotListItem {
  const trainerUserId = row.trainer_user_id ?? row.trainer_id ?? null;
  return {
    ...row,
    trainer_user_id: trainerUserId,
    trainer_id: trainerUserId,
    branch_id: row.branch_id ?? (branchId != null ? branchId : null) ?? null
  };
}

/**
 * GET /api/v1/schedule/slots?manage=true — тренер: слоты филиала, где он ведущий (staff JWT).
 * `organizationId` — выбранная студия (`me.organization_id`), чтобы суперадмин не терял контекст.
 */
export function fetchTrainerManageSlots(params: {
  organizationId?: number | null;
  branchId: number | null;
  fromDate: string;
  toDate: string;
  accessToken: string;
}): Promise<ScheduleSlotListItem[]> {
  const q: QueryRecord = {
    from_date: params.fromDate,
    to_date: params.toDate,
    manage: true
  };
  if (params.organizationId != null && params.organizationId > 0) {
    q.organization_id = params.organizationId;
  }
  if (params.branchId != null) {
    q.branch_id = params.branchId;
  }
  return apiGet<ScheduleSlotListItem[]>('/api/v1/schedule/slots', q, {
    accessToken: params.accessToken
  }).then((rows) =>
    rows.map((s) => normalizeScheduleSlotListItem(s, params.branchId))
  );
}

/** manage=true по всем филиалам из `me.trainer_placements` сразу (сетка тренера). */
export async function fetchTrainerManageSlotsAllBranches(params: {
  organizationId?: number | null;
  branchList: { id: number; name: string }[];
  fromDate: string;
  toDate: string;
  accessToken: string;
}): Promise<ScheduleSlotListItem[]> {
  return fetchScheduleSlotsAllBranches({
    organizationId: params.organizationId ?? null,
    branchList: params.branchList,
    fromDate: params.fromDate,
    toDate: params.toDate,
    accessToken: params.accessToken,
    manage: true
  });
}

export function deleteScheduleSlot(slotId: number, accessToken: string): Promise<void> {
  return apiDelete<void>(`/api/v1/schedule/slots/${slotId}`, { accessToken });
}

function withOptionalOrgQuery(path: string, organizationId: number | null | undefined): string {
  if (organizationId == null || !Number.isFinite(organizationId) || organizationId < 1) {
    return path;
  }
  const joiner = path.includes('?') ? '&' : '?';
  return `${path}${joiner}organization_id=${organizationId}`;
}

/** Тело POST — ключи PascalCase (`domain.ScheduleSlotWrite`). */
export type ScheduleSlotCreateBody = {
  ServiceID: number;
  TrainerUserID?: number;
  BranchID?: number;
  SlotDate: string;
  StartTime: string;
  EndTime: string;
  MaxParticipants: number;
  IsAvailable: boolean;
};

/** POST /api/v1/schedule/slots — создание слота (staff). */
export function createScheduleSlot(
  accessToken: string,
  body: ScheduleSlotCreateBody,
  organizationId?: number | null
): Promise<ScheduleSlotListItem> {
  const path = withOptionalOrgQuery('/api/v1/schedule/slots', organizationId);
  return apiPost<ScheduleSlotListItem>(path, body, { accessToken }).then((row) =>
    normalizeScheduleSlotListItem(row, body.BranchID ?? null)
  );
}

/** PATCH /api/v1/schedule/slots/{slot_id} — частичное обновление (PascalCase полей patch). */
export function patchScheduleSlot(
  accessToken: string,
  slotId: number,
  patch: Record<string, unknown>,
  opts?: { organizationId?: number | null; detach_from_recurring?: boolean }
): Promise<ScheduleSlotListItem> {
  let path = `/api/v1/schedule/slots/${slotId}`;
  const q: string[] = [];
  if (opts?.organizationId != null && opts.organizationId > 0) {
    q.push(`organization_id=${opts.organizationId}`);
  }
  if (opts?.detach_from_recurring) {
    q.push('detach_from_recurring=1');
  }
  if (q.length) path += `?${q.join('&')}`;
  return apiPatch<ScheduleSlotListItem>(path, patch, { accessToken }).then((row) =>
    normalizeScheduleSlotListItem(row, row.branch_id ?? null)
  );
}

export type ScheduleSlotMoveBody = {
  slot_date: string;
  start_time: string;
  end_time: string;
};

/** POST …/move/validate */
export function validateScheduleSlotMove(
  accessToken: string,
  slotId: number,
  body: ScheduleSlotMoveBody,
  organizationId?: number | null
): Promise<void> {
  const path = withOptionalOrgQuery(`/api/v1/schedule/slots/${slotId}/move/validate`, organizationId);
  return apiPost<void>(path, body, { accessToken });
}

/** POST …/move */
export function moveScheduleSlot(
  accessToken: string,
  slotId: number,
  body: ScheduleSlotMoveBody,
  organizationId?: number | null
): Promise<void> {
  const path = withOptionalOrgQuery(`/api/v1/schedule/slots/${slotId}/move`, organizationId);
  return apiPost<void>(path, body, { accessToken });
}

export type ScheduleSlotNotifyBody = {
  event_type: string;
  message?: string;
  target_roles?: string[];
};

/** POST /api/v1/schedule/slots/{slot_id}/notify */
export function notifyScheduleSlot(
  accessToken: string,
  slotId: number,
  body: ScheduleSlotNotifyBody
): Promise<{ ok: boolean; enqueued?: number; dry_run?: boolean; detail?: string }> {
  return apiPost(`/api/v1/schedule/slots/${slotId}/notify`, body, { accessToken });
}

/** GET /api/v1/schedule/slots/{slot_id} */
export function fetchScheduleSlotByID(
  slotId: number,
  accessToken: string
): Promise<ScheduleSlotListItem> {
  return apiGet<ScheduleSlotListItem>(`/api/v1/schedule/slots/${slotId}`, undefined, { accessToken });
}
